<template>
	<Modal
		v-if="stepComponent"
		max-width="sm:max-w-[485px]"
		:hide-actions="true"
		:allow-close="false"
		:remove-padding="true"
		rounded-size="rounded-[10px]"
		:prevent-scrolling="false"
	>
		<template #body>
			<div
				v-if="step !== STEPS.PROMO"
				class="flex justify-end pt-2 pr-2"
			>
				<button
					type="button"
					class="flex size-9 cursor-pointer items-center justify-center rounded-full hover:bg-black/5"
					aria-label="Close"
					@click="store.closeGate"
				>
					<XMarkIcon
						class="size-5 text-[#111827]"
						aria-hidden="true"
					/>
				</button>
			</div>

			<component :is="stepComponent" />
		</template>
	</Modal>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { XMarkIcon } from '@heroicons/vue/16/solid';

import Modal from '../../components/modal.vue';
import PromoStep from './steps/promo-step.vue';
import UploadResumeStep from './steps/upload-resume-step.vue';
import LinkedInInstructionsStep from './steps/linkedin-instructions-step.vue';
import StaleResumeStep from './steps/stale-resume-step.vue';
import { setAuthState } from '../../composables/auth';
import { useHideSupportWidget } from '../../composables/useHideSupportWidget';
import { useAutoApplyPromoStore, STEPS } from '../../store/auto-apply-promo.store';

/**
 * The existing-user AutoApply promo and Resume Gate (EN-776), mounted globally so it greets
 * the user in whichever section they land. `STEPS.ANALYSIS` has no body here yet: Resume
 * Analysis and Qualification (EN-777) plug in by adding theirs to STEP_COMPONENTS.
 */
const STEP_COMPONENTS = {
	[STEPS.PROMO]: PromoStep,
	[STEPS.UPLOAD]: UploadResumeStep,
	[STEPS.LINKEDIN]: LinkedInInstructionsStep,
	[STEPS.CONFIRM]: StaleResumeStep,
};

const route = useRoute();
const store = useAutoApplyPromoStore();
const { step } = storeToRefs(store);
const { isAuthenticated, isOnboardingComplete, getUser } = setAuthState();

const stepComponent = computed(() => STEP_COMPONENTS[step.value] ?? null);

// Checked on every navigation (the same trigger as the premium nudge in App.vue) so an
// in-app login that swaps the cached user without a page load still gets prompted. Accounts
// still inside the legacy onboarding wizard wait: the router pins them to /onboarding, so the
// gate's ResumAI link would bounce straight back, and only the v3 flow ends the promo itself.
watch(() => route.name, () => {
	if (
		step.value
		|| !isAuthenticated()
		|| !isOnboardingComplete()
		|| route.meta?.isPreview
		|| route.meta?.suppressGlobalPrompts
	) {
		return;
	}

	const promptState = getUser()?.auto_apply_promo;
	if (promptState?.show) {
		store.open(promptState);
	}
}, { immediate: true });

// The Zoho chat bubble overlaps the bottom-anchored modal on mobile.
useHideSupportWidget(() => !!stepComponent.value);
</script>
